import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  FinancialWorkspacePayrollConceptsDocument,
  FinancialWorkspacePayrollConceptsQuery,
  PayrollConceptFragment,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { EntityFilter } from 'modules/finance/components/FinancialWorkspacePayroll/components/Filters/EntityFilter'

interface PayrollLineFilterProps {
  defaultOpen?: boolean
  defaultConceptIds?: number[]
}

const Item = ({ concept }: { concept: PayrollConceptFragment }) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center" key={concept.id}>
      <Text.Normal size="300">{concept.label}</Text.Normal>
    </Box>
  )
}

export const PayrollLineFilter = ({ defaultOpen, defaultConceptIds }: PayrollLineFilterProps) => {
  const { state, dispatch } = useFinancialWorkspace()
  const { saveFilters } = useFilterProvider()

  const extractConnection = (data: FinancialWorkspacePayrollConceptsQuery) => {
    return data.payroll.conceptsConnection
  }

  const renderEntity = (entity: PayrollConceptFragment) => <Item concept={entity} />

  const renderSkeleton = () => {
    return (
      <Box gap="s8" flexDirection="row" alignItems="center">
        <Skeleton width="s240" height="s32" />
      </Box>
    )
  }

  const onValueChange = (value: PayrollConceptFragment[]) => {
    const payload = value.map((o) => o.id)
    dispatch({ type: 'SET_CONCEPT_IDS', payload })
    saveFilters('conceptIds', payload)
  }

  // set default value as current value if there is no current value
  useUnsafeEffect(() => {
    if (defaultConceptIds?.length && !state.conceptIds?.length) {
      dispatch({ type: 'SET_CONCEPT_IDS', payload: [...defaultConceptIds] })
    }
  }, [dispatch, defaultConceptIds, state.conceptIds])

  const filterEntities = (entities: PayrollConceptFragment[]) => entities

  const value: PayrollConceptFragment[] =
    useUnsafeMemo(
      () =>
        state.conceptIds?.map((id) => ({
          id,
          // Only the id is used, the rest are dummy values to make the type checker happy
          name: id.toString(),
          label: id.toString(),
          companyId: 0,
          default: false,
          description: '',
        })),
      [state.conceptIds]
    ) || []

  return (
    <EntityFilter
      value={value}
      onChange={onValueChange}
      defaultOpen={defaultOpen}
      label={i18n.t('finance.financial_workspace.payroll.filters.concepts')}
    >
      <EntitySelector.Multiple
        alwaysOpen
        hideBorder
        variables={{}}
        document={FinancialWorkspacePayrollConceptsDocument}
        hideSearchIcon
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        value={value}
        onChange={onValueChange}
        renderSkeleton={renderSkeleton}
        renderValue={(_: PayrollConceptFragment[]) => <></>}
        filterEntities={filterEntities}
      />
    </EntityFilter>
  )
}
